/**
 * Форматирует дату из ISO формата в формат DD.MM.YYYY HH:MM
 * @param {string} isoDateString - Дата в формате ISO (например, "2025-06-06T09:44:51.075269Z")
 * @returns {string} - Дата в формате "06.06.2025 14:44"
 */
export const formatDateTime = (isoDateString) => {
  if (!isoDateString) return '-';

  try {
    const date = new Date(isoDateString);

    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) return isoDateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch (error) {
    console.error('Ошибка форматирования даты:', error);
    return isoDateString;
  }
};

/**
 * Форматирует дату из ISO формата в формат DD.MM.YYYY
 * @param {string} isoDateString - Дата в формате ISO
 * @returns {string} - Дата в формате "06.06.2025"
 */
export const formatDate = (isoDateString) => {
  if (!isoDateString) return '-';

  try {
    const date = new Date(isoDateString);

    if (isNaN(date.getTime())) return isoDateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error('Ошибка форматирования даты:', error);
    return isoDateString;
  }
};

/**
 * Вычисляет цвет подсветки строки в зависимости от возраста заявки
 * @param {string} isoDateString - Дата создания в формате ISO
 * @returns {string} - CSS цвет для фона строки
 */
const ROW_HIGHLIGHT_SCHEME = [
  {
    maxDays: 45,
    base: 'rgba(254, 252, 232, 0.92)',
    tint: 'rgba(255, 249, 228, 0.88)',
    accent: 'rgba(250, 204, 21, 0.85)',
    glow: 'rgba(250, 204, 21, 0.16)',
  },
  {
    maxDays: 90,
    base: 'rgba(255, 247, 237, 0.94)',
    tint: 'rgba(255, 238, 224, 0.9)',
    accent: 'rgba(251, 146, 60, 0.85)',
    glow: 'rgba(251, 146, 60, 0.18)',
  },
  {
    maxDays: 120,
    base: 'rgba(255, 242, 238, 0.95)',
    tint: 'rgba(255, 234, 228, 0.9)',
    accent: 'rgba(249, 115, 22, 0.8)',
    glow: 'rgba(249, 115, 22, 0.2)',
  },
  {
    maxDays: Infinity,
    base: 'rgba(255, 239, 242, 0.96)',
    tint: 'rgba(255, 231, 236, 0.92)',
    accent: 'rgba(244, 63, 94, 0.85)',
    glow: 'rgba(244, 63, 94, 0.22)',
  },
];

const resolveHighlightTier = (isoDateString) => {
  if (!isoDateString) return null;

  const createdDate = new Date(isoDateString);
  if (isNaN(createdDate.getTime())) return null;

  const now = new Date();
  const isSameMonth = createdDate.getMonth() === now.getMonth();
  const isSameYear = createdDate.getFullYear() === now.getFullYear();

  if (isSameMonth && isSameYear) {
    return null;
  }

  const diffTime = now - createdDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return null;

  const tier =
    ROW_HIGHLIGHT_SCHEME.find(({ maxDays }) => diffDays <= maxDays) ||
    ROW_HIGHLIGHT_SCHEME[ROW_HIGHLIGHT_SCHEME.length - 1];

  return { tier, diffDays };
};

export const getRowBackgroundColor = (isoDateString) => {
  try {
    const highlight = resolveHighlightTier(isoDateString);
    return highlight?.tier?.base || 'transparent';
  } catch (error) {
    console.error('Ошибка вычисления цвета строки:', error);
    return 'transparent';
  }
};

/**
 * Возвращает стили для подсветки строки таблицы с мягким градиентом
 * @param {string} isoDateString - Дата создания в формате ISO
 * @returns {object|undefined} - Inline-стили для строки
 */
export const getRowHighlightStyle = (isoDateString) => {
  try {
    const highlight = resolveHighlightTier(isoDateString);
    if (!highlight) return undefined;

    const { tier } = highlight;

    return {
      background: `linear-gradient(90deg, ${tier.base} 0%, ${tier.tint} 55%, rgba(255, 255, 255, 0) 100%)`,
      boxShadow: `inset 4px 0 0 ${tier.accent}, 0 1px 4px ${tier.glow}`,
    };
  } catch (error) {
    console.error('Ошибка вычисления стиля строки:', error);
    return undefined;
  }
};

/**
 * Определяет, является ли дата просроченной (старше 30 дней)
 * @param {string} isoDateString - Дата создания в формате ISO
 * @returns {boolean} - true если дата старше 30 дней
 */
export const isOverdue = (isoDateString) => {
  if (!isoDateString) return false;

  try {
    const createdDate = new Date(isoDateString);

    if (isNaN(createdDate.getTime())) return false;

    const now = new Date();
    const diffTime = now - createdDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 30;
  } catch (error) {
    console.error('Ошибка проверки просрочки:', error);
    return false;
  }
};
